import React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { ArrayExtensions, createInventoryAvailabilitySearchCriteria, DeliveryMode, IProductInventoryInformation, mapAggregatedProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { ProductListProduct } from '../product-list-component/product-list-component';
import { ProductSearchResultExt } from '../../shared-utils/types';
import { ProductsDataActions, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { applyAttributesToProduct } from '../../shared-utils/attributes';
import { getProductUnitOfMeasureOptionsAsync } from '../../actions/get-product-unit-of-measure-options.action';
import { getBrandSchemesByIdsAsync } from '../../actions/get-brand-schemes.action';
import { IBrandSchemeEntity } from '../../actions/entities/brand-scheme-entity';
import { IProductRelationTypeForProduct } from '../../actions/get-relation-types-by-ids';
import { AlternativeProductLine } from './alternative-product-line';

export interface IAlternativeProductsResources {
    alternativeProductsLoadingText: string;
    alternativeProductsErrorText: string;
    alternativeProductsNoResultsText: string;
    alternativeProductsChangeProductButtonText: string;
}

type AlternativeProductsProps = {
    id: string;
    context: ICoreContext;
    typeName: string;
    resources: IAlternativeProductsResources;
    product: ProductListProduct;
    relationTypesByIds?: IProductRelationTypeForProduct[];
    onChangeProduct?: (product: ProductListProduct, replacement: ProductListProduct) => void;
};

export function AlternativeProducts({
    id,
    context,
    typeName,
    resources,
    product,
    relationTypesByIds,
    onChangeProduct
}: AlternativeProductsProps) {
    const [state, setState] = React.useState<'loading' | 'error' | 'no-results' | 'done'>('loading');
    const [products, setProducts] = React.useState<ProductSearchResultExt[]>([]);
    const [simpleProducts, setSimpleProducts] = React.useState<SimpleProduct[]>([]);
    const [unitOfMeasureConversions, setUnitOfMeasureConversions] = React.useState<UnitOfMeasureConversion[]>([]);
    const [_brandSchemes, setBrandSchemes] = React.useState<IBrandSchemeEntity[]>([]);
    const [estimatedAvailabilities, setEstimatedAvailabilities] = React.useState<IProductInventoryInformation[]>([]);

    React.useEffect(() => {
        setState('loading');

        void (async () => {
            const { channelId, catalogId } = context.request.apiSettings;
            const relationTypes = relationTypesByIds?.filter(x => x.ProductId === product.RecordId);

            if (!relationTypes || relationTypes.length === 0) {
                setState('no-results');
                return;
            }

            const promises = relationTypes.map(relationType => ProductsDataActions.getRelatedProductsAsync(
                { callerContext: context.actionContext },
                product.RecordId,
                channelId,
                catalogId,
                relationType.RecordId!
            ));

            const allRelatedProducts = await Promise.all(promises);
            const result: ProductSearchResultExt[] = [];

            allRelatedProducts.forEach((p) => {
                result.push(...p);
            });

            if (result.length === 0) {
                setState('no-results');
                return;
            }

            // Sort by DisplayOrder ascending
            result.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

            result.forEach((p) => {
                applyAttributesToProduct(context.request, p, p.AttributeValues, undefined);
            });

            setProducts(result);

            const pricesPromise = ProductsDataActions.getActivePricesAsync(
                { callerContext: context.actionContext },
                {
                    CatalogId: catalogId,
                    ChannelId: channelId
                },
                result.map(p => p.RecordId),
                new Date(),
                null,
                undefined,
                true
            );
            const simpleProductsPromise = ProductsDataActions.getByIdsAsync(
                { callerContext: context.actionContext },
                channelId,
                result.map(p => p.RecordId),
                null,
                catalogId
            );
            const unitOfMeasureConversionsPromise = getProductUnitOfMeasureOptionsAsync({ callerContext: context.actionContext }, result.map(p => p.RecordId));
            const brandSchemesIds = ArrayExtensions.unique(result.flatMap(x => x.SpecialBadges?.split('|')).filter(Boolean) || []) as string[];
            const brandSchemesPromise = brandSchemesIds.length === 0 ? Promise.resolve([]) : getBrandSchemesByIdsAsync({ callerContext: context.actionContext }, brandSchemesIds);
            const estimatedAvailabilitiesPromise = ProductsDataActions.getEstimatedAvailabilityAsync(
                { callerContext: context.actionContext },
                createInventoryAvailabilitySearchCriteria(context.actionContext, result.map(p => p.RecordId), true, undefined, undefined, DeliveryMode.shipping)
            );

            const [
                prices,
                simpleProducts,
                unitOfMeasureConversions,
                brandSchemes,
                estimatedAvailabilities
            ] = await Promise.all([pricesPromise, simpleProductsPromise, unitOfMeasureConversionsPromise, brandSchemesPromise, estimatedAvailabilitiesPromise]);

            result.forEach((p) => {
                p.ProductPrice = prices.find(price => price.ProductId === p.RecordId);
            });

            setUnitOfMeasureConversions(unitOfMeasureConversions);
            setBrandSchemes(brandSchemes);
            setProducts(result);
            setSimpleProducts(simpleProducts);

            if (estimatedAvailabilities.ProductWarehouseInventoryAvailabilities) {
                const shippingInventory = mapAggregatedProductInventoryInformation(context.actionContext, estimatedAvailabilities);
                if (ArrayExtensions.hasElements(shippingInventory)) {
                    shippingInventory.forEach((x) => {
                        x.deliveryType = DeliveryMode.shipping;
                        x.ProductAvailableQuantity.ExtensionProperties = estimatedAvailabilities.ProductWarehouseInventoryAvailabilities
                            ?.find(y => y.ProductId === x.ProductAvailableQuantity.ProductId)?.ExtensionProperties;
                    });
                    setEstimatedAvailabilities(shippingInventory);
                }
            }

            setState('done');
        })();
    }, [product]);

    return (
        <div className='dlvry-alternative-products-popup'>
            {state === 'loading' && (
                <div>{resources.alternativeProductsLoadingText}</div>
            )}
            {state === 'error' && (
                <div>{resources.alternativeProductsErrorText}</div>
            )}
            {state === 'no-results' && (
                <div>{resources.alternativeProductsNoResultsText}</div>
            )}
            {state === 'done' && (
                <ul className='dlvry-alternative-products-popup__list'>
                    {products?.slice(0, 3).map((p) => (
                        <AlternativeProductLine
                            id={id}
                            typeName={typeName}
                            key={p.RecordId}
                            context={context}
                            resources={resources}
                            product={p}
                            simpleProduct={simpleProducts.find(sp => sp.RecordId === p.RecordId)}
                            unitConversions={unitOfMeasureConversions}
                            availability={estimatedAvailabilities.find(ea => ea.ProductAvailableQuantity.ProductId === p.RecordId)}
                            onChangeProduct={(replacement) => {
                                if (!onChangeProduct) {
                                    return;
                                }

                                // Copy UOM from simple product to replacement product
                                const replacementSimpleProduct = simpleProducts.find(sp => sp.RecordId === replacement.RecordId);
                                if (replacementSimpleProduct) {
                                    replacement.DefaultUnitOfMeasure = replacementSimpleProduct.DefaultUnitOfMeasure;
                                }
                                onChangeProduct?.(product, replacement);
                            }}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}
